/**
 * Security Articles
 */

import type { HelpArticle } from "../types";

export const SECURITY_ARTICLES: HelpArticle[] = [
  {
    id: "sec-001",
    slug: "how-we-protect-your-funds",
    title: "How We Protect Your Funds",
    description: "Security measures to keep your assets safe",
    category: "security",
    popular: true,
    order: 1,
    keywords: ["protection", "safety", "secure", "custody", "insurance"],
    content: `
# How We Protect Your Funds

Your security is our top priority. Here's how we keep your funds safe.

## Asset Security

### Cryptocurrency
- Industry-leading custody partners
- Multi-signature wallets
- Cold storage for reserves
- Regular security audits

### Fiat Currency
- Held at FDIC-insured banks
- Segregated client accounts
- No commingling of funds
- Daily reconciliation

## Platform Security

### Infrastructure
- SOC 2 Type II certified
- AWS hosted with encryption
- 99.9% uptime SLA
- 24/7 monitoring

### Application Security
- End-to-end encryption
- Regular penetration testing
- Bug bounty program
- Code audits

## Account Security

### Authentication
- Passwordless (magic links)
- Two-factor authentication
- Device verification
- Session management

### Access Control
- IP allowlisting (optional)
- Withdrawal address whitelisting
- Cooling periods for changes
- Activity notifications

## Compliance

- Licensed money transmitter (where required)
- AML/KYC compliance
- Sanctions screening
- Transaction monitoring
- Regulatory reporting

## Insurance

- Crime insurance coverage
- Cyber insurance
- E&O insurance
- Coverage details available on request

## What We Don't Do

Store passwords (passwordless)
Hold your private keys
Share data without consent
Process suspicious transactions
    `.trim(),
  },
  {
    id: "sec-002",
    slug: "recognizing-phishing",
    title: "Recognizing Phishing",
    description: "Protect yourself from phishing attacks",
    category: "security",
    order: 2,
    keywords: ["phishing", "scam", "fraud", "fake", "attack"],
    content: `
# Recognizing Phishing

Learn to identify and avoid phishing attempts.

## What Is Phishing?

Phishing is when attackers try to trick you into:
- Revealing login credentials
- Sending money to fake addresses
- Installing malicious software
- Providing personal information

## Common Phishing Methods

### Fake Emails
- Looks like from Supabank
- Urgent language
- Suspicious links
- Requests sensitive info

### Fake Websites
- Similar domain names
- Copied design
- Asks for login
- No HTTPS

### Social Media
- Fake support accounts
- DM requests for info
- Fake giveaways
- Impersonation

### Phone Calls
- Claims to be Supabank
- Requests verification codes
- Pressures immediate action
- Threatens account closure

## How to Identify Phishing

### Check Email Address
- Official: @supabank.xyz
- Suspicious: @supabank-support.com

### Check Links (before clicking)
- Hover to see destination
- Official: supabank.xyz
- Suspicious: supabank.xyz.fake.com

### Red Flags
⚠️ Urgent/threatening language
⚠️ Spelling/grammar errors
⚠️ Requests for passwords
⚠️ Unexpected attachments
⚠️ "Act now" pressure

## What We'll Never Ask

Supabank will NEVER:
- Ask for your password
- Request 2FA codes via email
- Ask you to send crypto "for verification"
- Threaten account closure for non-payment
- Contact you on social media for support

## If You Suspect Phishing

1. Don't click any links
2. Don't reply or provide info
3. Report to security@supabank.xyz
4. Forward suspicious email
5. Delete the message
    `.trim(),
  },
  {
    id: "sec-003",
    slug: "reporting-suspicious-activity",
    title: "Reporting Suspicious Activity",
    description: "How to report security concerns",
    category: "security",
    order: 3,
    keywords: ["report", "suspicious", "fraud", "alert", "notify"],
    content: `
# Reporting Suspicious Activity

See something concerning? Here's how to report it.

## What to Report

### On Your Account
- Transactions you didn't make
- Login from unknown location
- Changed settings you didn't change
- Missing funds

### Phishing/Scams
- Suspicious emails
- Fake websites
- Social media impersonation
- Phone call scams

### Security Vulnerabilities
- Website bugs
- Application issues
- Security weaknesses
- Data exposure

## How to Report

### Account Concerns
**Urgent** (unauthorized access):
1. Email: security@supabank.xyz
2. Include: Account email, description
3. We respond within 1 hour

**Non-urgent**:
1. Email: support@supabank.xyz
2. Or use in-app support
3. Response within 24 hours

### Phishing Reports
1. Email: security@supabank.xyz
2. Subject: "Phishing Report"
3. Include: Screenshot or forwarded email
4. We investigate immediately

### Security Vulnerabilities
Bug bounty program:
1. Email: security@supabank.xyz
2. Describe the vulnerability
3. Include steps to reproduce
4. Rewards available for valid reports

## What Happens After Reporting

1. **Immediate acknowledgment**
2. **Investigation begins**
3. **Account secured if needed**
4. **Regular updates provided**
5. **Resolution and explanation**

## Protecting Your Account

While we investigate:
- Change email password
- Enable/reset 2FA
- Review recent transactions
- Check connected devices
    `.trim(),
  },
  {
    id: "sec-004",
    slug: "lost-device-procedure",
    title: "Lost Device Procedure",
    description: "What to do if you lose access to your device",
    category: "security",
    order: 4,
    keywords: ["lost", "device", "phone", "stolen", "access"],
    content: `
# Lost Device Procedure

Lost your phone or computer? Here's what to do immediately.

## Immediate Steps

### 1. Secure Your Email
- Change email password
- Enable 2FA on email
- Review email access logs

### 2. Revoke Supabank Sessions
On another device:
1. Login to supabank.xyz
2. Go to Settings > Security
3. Click "Sign Out All Devices"
4. Confirm with 2FA

### 3. Review Account Activity
- Check recent transactions
- Look for unauthorized activity
- Review login history

## If 2FA Device Lost

### With Backup Codes
1. Go to login page
2. Enter email for magic link
3. At 2FA prompt, click "Use backup code"
4. Enter backup code
5. Login and reset 2FA

### Without Backup Codes
1. Email support@supabank.xyz
2. Subject: "2FA Recovery - Lost Device"
3. Include:
   - Account email
   - Full name
   - Date of birth
   - Recent transaction details
4. Complete identity verification
5. 2FA will be reset

## Prevention Tips

### Before It Happens
Save backup codes offline
Use authenticator app with cloud backup
Keep email super secure
Know your account details

### Regular Maintenance
Review active sessions monthly
Update recovery info
Test backup codes work
Keep contact info current

## Timeline

| Action | Time |
|--------|------|
| Session revocation | Immediate |
| Email security review | Immediate |
| 2FA recovery (with codes) | 5 minutes |
| 2FA recovery (support) | 24-48 hours |
    `.trim(),
  },
  {
    id: "sec-005",
    slug: "api-key-security",
    title: "API Key Security",
    description: "Best practices for API key management",
    category: "security",
    order: 5,
    keywords: ["api", "key", "integration", "developer", "secret"],
    content: `
# API Key Security

Protect your API keys and integrations.

## API Key Basics

### What API Keys Do
- Authenticate API requests
- Identify your application
- Track usage and limits
- Control access permissions

### Key Types
- **Read-only**: View data only
- **Full access**: Read and write
- **Limited**: Specific actions only

## Security Best Practices

### Storage
Use environment variables
Use secret managers (AWS Secrets, Vault)
Never commit to version control
Encrypt at rest

### Access
Limit who has access
Use least privilege principle
Audit access regularly
Rotate keys periodically

### Usage
Use HTTPS only
Implement rate limiting
Monitor for anomalies
Log API calls

## What NOT To Do

Hardcode keys in source code
Share keys via email/chat
Use production keys in development
Store in public repositories
Share keys between applications

## Key Rotation

### When to Rotate
- Scheduled (every 90 days)
- After employee departure
- After security incident
- When key may be compromised

### How to Rotate
1. Generate new key in Settings
2. Update your application
3. Test thoroughly
4. Deactivate old key
5. Monitor for issues

## If Key Is Compromised

1. **Immediately revoke the key**
2. Generate new key
3. Update application
4. Audit API logs
5. Report to security@supabank.xyz

## Monitoring

- Review API logs regularly
- Set up usage alerts
- Watch for unusual patterns
- Monitor error rates
    `.trim(),
  },
  {
    id: "sec-006",
    slug: "compliance-aml",
    title: "Compliance & AML",
    description: "Our regulatory compliance practices",
    category: "security",
    order: 6,
    keywords: ["compliance", "aml", "regulations", "legal", "kyc"],
    content: `
# Compliance & AML

How we maintain regulatory compliance and prevent financial crime.

## Regulatory Framework

### Licenses
- Money Transmitter licenses (US states)
- European compliance (pending)
- Regular audits and examinations

### Standards
- BSA/AML compliance
- OFAC sanctions screening
- FinCEN reporting
- State-specific requirements

## Know Your Customer (KYC)

### Why We Verify Identity
- Regulatory requirement
- Fraud prevention
- Account security
- Financial crime prevention

### What We Verify
- Government ID
- Selfie match
- Address (enhanced tiers)
- Source of funds (high volume)

## Anti-Money Laundering (AML)

### Transaction Monitoring
- Real-time screening
- Pattern analysis
- Risk scoring
- Suspicious activity detection

### What We Screen For
- Sanctions lists (OFAC, UN, EU)
- Politically exposed persons (PEPs)
- Adverse media
- High-risk jurisdictions

## Your Responsibilities

### Required Disclosures
- Accurate personal information
- Source of funds (if asked)
- Business purpose (business accounts)
- Beneficial ownership

### Prohibited Activities
Money laundering
Terrorist financing
Sanctions evasion
Tax evasion
Fraud

## Privacy & Data

### What We Collect
- Identity documents
- Transaction records
- Communication logs
- Device information

### How We Protect It
- Encrypted storage
- Limited access
- Retention policies
- Right to deletion (where permitted)

## Questions?

Contact compliance@supabank.xyz for compliance-related inquiries.
    `.trim(),
  },
];
